import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import useAsyncRequest from "hooks/useAsyncRequest";
import { LoadablePage } from "shared/LoadablePage";
import { formatUserName } from "client/api/UserGrantApi";
import type { BaseUserGrantsApi, CreateUserGrantRequest, UserGrantDto } from "client/api/UserGrantApi";
import { ActionDialog } from "components/ActionDialog/ActionDialog";
import { Overflow, OverflowMenu } from "components/OverflowMenu/OverflowMenu";
import { ErrorAlert } from "components/alert/ErrorAlert";
import type { RbacPolicy } from "components/authorization/RbacPolicies";
import { UserRoutesMap } from "areas/users/UserRoutesMap";

export type UserGrantsViewProps<TUserGrant extends UserGrantDto, TCreateRequest extends CreateUserGrantRequest> = {
  assetId: string;
  userGrantsApi: BaseUserGrantsApi<TUserGrant, TCreateRequest>;
  requiredWriteRole: RbacPolicy;
};

export function UserGrantsView<TUserGrant extends UserGrantDto, TCreateRequest extends CreateUserGrantRequest>(
  props: UserGrantsViewProps<TUserGrant, TCreateRequest>
) {
  const { assetId, userGrantsApi, requiredWriteRole } = props;

  const [obsoleteGrant, setObsoleteGrant] = useState<UserGrantDto | undefined>(undefined);
  const [grantDeletionError, setGrantDeletionError] = useState<Error | undefined>(undefined);
  const showDeleteDialog = (userGrant: UserGrantDto) => {
    setObsoleteGrant(userGrant);
  };
  const navigate = useNavigate();

  const deleteUserGrant = async () => {
    if (!obsoleteGrant) {
      return;
    }
    setGrantDeletionError(undefined);

    try {
      await userGrantsApi.deleteUserGrant(assetId, obsoleteGrant.id);
    } catch (ex) {
      if (ex instanceof Error) {
        setGrantDeletionError(ex);
      }
    }

    refresh();

    setObsoleteGrant(undefined);
  };

  const onDeleteCancelled = () => {
    setObsoleteGrant(undefined);
  };

  const loadGrants = useMemo(() => (id: string) => userGrantsApi.listUserGrants(id), [userGrantsApi]);
  const { data, loading, error, refresh } = useAsyncRequest(loadGrants, assetId);

  return (
    <LoadablePage loading={loading} error={error}>
      {grantDeletionError && <ErrorAlert error={grantDeletionError} />}
      <TableContainer component={Box}>
        <Table size={"small"}>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell sx={{ width: "300px" }}>Role</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.directUserGrants.map((ug, i) => (
              <TableRow key={i}>
                <TableCell>{formatUserName(ug.user)}</TableCell>
                <TableCell sx={{ width: "300px" }}>{ug.role.name}</TableCell>
                <TableCell style={{ width: "40px" }}>
                  <OverflowMenu
                    items={[
                      Overflow.menuItem("View user details", () => navigate(UserRoutesMap.detail(ug.user.id).root)),
                      Overflow.policyAuthorizedMenuItem(requiredWriteRole, "Delete", () => showDeleteDialog(ug)),
                    ]}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ActionDialog
        title={"Delete user grant?"}
        description={"Do you wish to delete this user grant?"}
        show={!!obsoleteGrant}
        confirmText={"Delete"}
        onConfirm={deleteUserGrant}
        onCancel={onDeleteCancelled}
      />
    </LoadablePage>
  );
}
